import { DownloadOutlined } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import MainContentContainer from '@/components/layouts/MainContentContainer'
import { useAlertContext } from '@/contexts/AlertContext'
import { WholesaleLayoutRouteInformation } from '@/features/wholesale/constants/routes'
import { useProductionPlanExport } from '@/features/wholesale/hooks/useProductionPlanExport'
import { errorHandler } from '@/utils/errorHandler'

import { useWholesaleLayout } from './WholesaleLayout'

export default function ProductionPlanExport() {
  const { t, i18n } = useTranslation()
  const { setPageConfig } = useWholesaleLayout()
  const { exportProductionPlan, isPending } = useProductionPlanExport()
  const { pushAlert } = useAlertContext()

  useEffect(() => {
    setPageConfig({
      title: t('component.page_header.production_plan_export'),
      breadcrumbs: [{ text: t('component.page_header.wholesale'), to: '/wholesale' }],
      pageTag: 'wholesale-production-plan-export',
      activeTab: WholesaleLayoutRouteInformation.PRODUCTION_PLAN_EXPORT.navigationPath,
    })
  }, [setPageConfig, t])

  const defaultDate = DateTime.now().plus({ days: 1 }).startOf('day')
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(defaultDate)

  async function handleDownload() {
    if (!selectedDate) return

    try {
      await exportProductionPlan(selectedDate)
      pushAlert({
        title: t('wholesale.production_plan_export.title'),
        message: t('wholesale.production_plan_export.download_success'),
        severity: 'success',
      })
    } catch (err) {
      const error = errorHandler(err, t('wholesale.production_plan_export.download_error'))
      pushAlert({ title: t('wholesale.production_plan_export.title'), message: error.message, severity: 'error' })
    }
  }

  return (
    <MainContentContainer>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <DatePicker
            label={t('common.date')}
            slotProps={{ textField: { inputProps: { 'data-testid': 'wholesale-production-plan-date-picker' } } }}
            sx={{ width: 200 }}
            value={selectedDate}
            onChange={setSelectedDate}
          />
        </LocalizationProvider>

        <Button
          disabled={!selectedDate || isPending}
          startIcon={<DownloadOutlined />}
          variant="contained"
          onClick={handleDownload}
        >
          {t('wholesale.production_plan_export.download_button')}
        </Button>
      </Stack>
    </MainContentContainer>
  )
}
